import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import NewLanding from './pages/NewLanding';
import Dashboard from './pages/Dashboard';
import Playground from './pages/Playground';
import Agents from './pages/Agents';
import CreateAgent from './pages/CreateAgent';
import Onboarding from './pages/Onboarding';
import Tools from './pages/Tools';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeBaseDetails from './pages/KnowledgeBaseDetails';
import PageTransition from './components/PageTransition';
import SignIn from './pages/SignIn';
import ContactUs from './pages/ContactUs';
import AgentDetails from './pages/AgentDetails';
import TalkToAgent from './pages/TalkToAgent';
import ProtectedLayout from './components/ProtectedLayout';
import Conversations from './pages/Conversations';
import ConversationDetails from './pages/ConversationDetails';
import ExampleWithAnalytics from './components/ExampleWithAnalytics';
import AuthTest from './components/AuthTest';
import AlphaAgent from './pages/AlphaAgent';
import { authService } from './services/authService';
import { Toaster } from './components/ui/sonner';
import analyticsManager, { initializeAnalytics } from './utils/analytics';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { AgentProvider } from './contexts/AgentContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Phone, Plus } from 'lucide-react';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';



// 21st.dev Toolbar imports
import { TwentyFirstToolbar } from '@21st-extension/toolbar-react';
import { ReactPlugin } from '@21st-extension/react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children}) => {
  const [authChecking, setAuthChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Wait for auth to initialize before checking login status
    const checkAuth = async () => {
      try {
        // Wait for Firebase auth to be ready
        await authService.waitForAuthInit();
        
        // Now check if user is logged in
        const loggedIn = authService.isLoggedIn();
        setIsAuthenticated(loggedIn);
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading spinner while checking auth
  if (authChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  // Phone Numbers page component
  const PhoneNumbers = () => (
    <div className="min-h-screen bg-background rounded-tl-lg -mr-8">
      <div className="bg-background border-b border-border sticky top-0 z-10 rounded-tl-lg">
        <div className="flex h-16 items-center justify-between px-8">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-card-foreground tracking-tight">Phone Numbers</h1>
          </div>
        </div>
      </div>
      <div className="px-8 py-8 space-y-8">
        <div className="flex justify-end">
          <Button size="sm" className="rounded-md">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Import number
          </Button>
        </div>
        <Card className="border border-border bg-card shadow-none">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center mb-4">
                <Phone className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold text-card-foreground mb-2">No phone numbers</h3>
              <p className="text-muted-foreground mb-6">You don't have any phone numbers yet.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Fixed Agents component to ensure it doesn't use useOutletContext
  const AgentsWrapper = () => {
    return (
      <div className="p-8">
        <Agents />
      </div>
    );
  };

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><NewLanding /></PageTransition>} />
        <Route path="/analytics-test" element={
          <PageTransition>
            <ExampleWithAnalytics />
          </PageTransition>
        } />
        <Route path="/auth-test" element={
          <PageTransition>
            <AuthTest />
          </PageTransition>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <Dashboard />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard/playground" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <Playground />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/playground" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <Playground />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/agents" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AgentsWrapper />
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/agents/new" element={
          <ProtectedRoute>
            <AgentProvider>
              <ProtectedLayout>
                <PageTransition>
                  <CreateAgent />
                </PageTransition>
              </ProtectedLayout>
            </AgentProvider>
          </ProtectedRoute>
        } />
        <Route path="/tools" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <Tools />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/knowledge-base" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <KnowledgeBase />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/knowledge-base/:documentId" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <KnowledgeBaseDetails />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/conversations" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <Conversations />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/conversations/:conversationId" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <ConversationDetails />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/phone-numbers" element={
          <ProtectedRoute>
            <ProtectedLayout>
            <PageTransition>
                <PhoneNumbers />
            </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/alpha-agent" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageTransition>
                <AlphaAgent />
              </PageTransition>
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <OnboardingProvider>
            <Onboarding />
          </OnboardingProvider>
        } />
        <Route path="/login" element={<SignIn />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/agents/:agentId/talk" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <TalkToAgent />
            </ProtectedLayout>
          </ProtectedRoute>
        } />
        <Route path="/agents/:agentId" element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AgentDetails />
            </ProtectedLayout>
          </ProtectedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
}

const App: React.FC = () => {
  useEffect(() => {
    // Initialize comprehensive analytics system
    initializeAnalytics();
    
    // Track app initialization
    analyticsManager.trackEvent('app_initialized', {
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      environment: process.env.NODE_ENV
    });
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="44agents-theme">
      {/* 21st.dev Toolbar - only renders in development mode */}
      {process.env.NODE_ENV === 'development' && (
        <TwentyFirstToolbar
          config={{
            plugins: [ReactPlugin],
          }}
        />
      )}
      
      <Router>
        <AnimatedRoutes />
      </Router>
      
      {/* Toast Notifications */}
      <Toaster />
    </ThemeProvider>
  );
};

export default App;
